import type { CSSProperties, SVGProps } from 'react';

export type IconName =
  | 'download' | 'chevronRight' | 'chevronLeft' | 'chevronDown'
  | 'arrowRight' | 'arrowUp' | 'arrowDown'
  | 'x' | 'plus' | 'search' | 'filter' | 'cog' | 'calendar'
  | 'user' | 'eye' | 'trash' | 'sidebar' | 'sidebarCollapsed'
  | 'refresh' | 'play' | 'alert' | 'check' | 'link' | 'copy'
  | 'sortAsc' | 'moreH' | 'barchart';

interface Props extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
  style?: CSSProperties;
}

const PATHS: Record<IconName, JSX.Element> = {
  download: (<><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M4 21h16" /></>),
  chevronRight: (<path d="M9 6l6 6-6 6" />),
  chevronLeft: (<path d="M15 6l-9 6 9 6" />),
  chevronDown: (<path d="M6 9l6 6 6-6" />),
  arrowRight: (<><path d="M5 12h14" /><path d="M13 5l7 7-7 7" /></>),
  arrowUp: (<path d="M12 19V5M5 12l7-7 7 7" />),
  arrowDown: (<path d="M12 5v14M5 12l7 7 7-7" />),
  x: (<><path d="M6 6l12 12" /><path d="M18 6L6 18" /></>),
  plus: (<><path d="M12 5v14" /><path d="M5 12h14" /></>),
  search: (<><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></>),
  filter: (<path d="M3 5h18M6 12h12M10 19h4" />),
  cog: (<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.55 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.55-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1-1.56V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9c.42.18.78.49 1.03.88.25.39.39.85.39 1.32" /></>),
  calendar: (<><rect x="3" y="5" width="18" height="16" rx="0" /><path d="M3 9h18M8 3v4M16 3v4" /></>),
  user: (<><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" /></>),
  eye: (<><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>),
  trash: (<><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></>),
  sidebar: (<><rect x="3" y="4" width="18" height="16" rx="0" /><path d="M9 4v16" /></>),
  sidebarCollapsed: (<><rect x="3" y="4" width="18" height="16" rx="0" /><path d="M9 4v16M13 9l3 3-3 3" /></>),
  refresh: (<><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" /></>),
  play: (<path d="M6 4l14 8-14 8V4z" />),
  alert: (<><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.86l-8 14A1.7 1.7 0 0 0 3.7 20.5h16.6a1.7 1.7 0 0 0 1.46-2.64l-8-14a1.7 1.7 0 0 0-2.92 0z" /></>),
  check: (<path d="M5 12l5 5L20 7" />),
  link: (<><path d="M10 14a5 5 0 0 0 7.07 0l3-3a5 5 0 1 0-7.07-7.07l-1.5 1.5" /><path d="M14 10a5 5 0 0 0-7.07 0l-3 3a5 5 0 1 0 7.07 7.07l1.5-1.5" /></>),
  copy: (<><rect x="8" y="8" width="13" height="13" rx="0" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" /></>),
  sortAsc: (<><path d="M3 6h12" /><path d="M3 12h9" /><path d="M3 18h6" /><path d="M19 5v14" /><path d="M16 8l3-3 3 3" /></>),
  moreH: (<><circle cx="5" cy="12" r="1.2" /><circle cx="12" cy="12" r="1.2" /><circle cx="19" cy="12" r="1.2" /></>),
  barchart: (<><path d="M3 21h18" /><rect x="5" y="13" width="3" height="6" /><rect x="11" y="9" width="3" height="10" /><rect x="17" y="5" width="3" height="14" /></>),
};

export function Icon({ name, size = 16, ...rest }: Props): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
